/* eslint-disable */
/* Marketing v2 — CRA entry.
   Tasarım dosyaları UMD-global React IIFE'leridir (window.MKT.*). Bunları CRA içinde
   çalıştırmak için global React'i shim'leyip dinamik import ile (shim'den SONRA) yüklüyoruz,
   ardından backend içeriğini yükleyip tasarımın boot App'ini render ediyoruz.
   Yönlendirme path-based: /<locale>/<route>/<arg> (history.pushState). Eski
   #/<locale>/<route> hash linkleri otomatik path'e migrate edilir. */
import React from "react";
import ReactDOM from "react-dom";
import "./styles.css";
import "./marketing.css";
import "./marketing-rtl.css";
import { loadMarketingData, ensureRouteData } from "./design/data-loader";

// UMD-tarzı tasarım dosyaları için global'leri shim'le
window.React = React;
window.ReactDOM = ReactDOM;

let bootPromise = null;
async function bootDesign() {
  // Performans: tasarım modülleri birbirine yalnızca RENDER anında erişir (eval
  // anında değil), bu yüzden seri import yerine PARALEL yüklüyoruz ve API
  // çağrısını import'larla eşzamanlı başlatıyoruz. Eskiden 10 import + fetch
  // seri zincirdi → mobil LCP'nin ana darboğazı. i18n önce (L/makeT/path).
  const dataP = loadMarketingData();
  // EVAL-anı bağımlılıkları sıralı: i18n önce; mock-flows window.MKT.Mock'u
  // EXTEND ettiği için mock.jsx ondan ÖNCE yüklenmeli (yoksa mock, Mock objesini
  // mock-flows'tan sonra ezip Screen'i düşürür → "Mock.Screen is not a function").
  await import("./design/i18n.js");
  await import("./design/mock.jsx");
  // Geri kalanlar birbirine yalnızca render anında erişir → paralel.
  await Promise.all([
    import("./design/seo.js"),
    import("./design/mock-flows.jsx"),
    import("./design/mock-rentgpt.jsx"),
    import("./design/Site.jsx"),
    import("./design/pages-main.jsx"),
    import("./design/pages-feature.jsx"),
    import("./design/pages-solution.jsx"),
    import("./design/pages-pricing.jsx"),
    import("./design/pages-content.jsx"),
  ]);
  await dataP;
}

// Path routing: /<locale>/<route>/<arg>. Locale lives in the path so every
// language + page is a distinct, crawlable, canonical URL (matches seo.js's
// canonical/hreflang). Replaces the old hash routing that collapsed every page
// into a single #-fragment URL invisible to search engines.
// Tüm desteklenen dil kodları. parsePath yol önekini bunlara göre tanır; yalnızca
// en/tr tanınırsa de/fr/es/it/pt/ar yollarında locale segmenti route sanılır →
// refresh'te /de/de gibi ikilenir ve detay sayfası kaybolur.
const LOCALE_CODES = ["en", "tr", "de", "fr", "es", "it", "pt", "ar"];

function parsePath() {
  const raw = (window.location.pathname || "/").replace(/^\/+/, "");
  const parts = raw.split("/").filter(Boolean);
  let locale = localStorage.getItem("mkt-locale") || "en";
  if (LOCALE_CODES.indexOf(parts[0]) !== -1) locale = parts.shift();
  return { locale, route: parts[0] || "home", arg: parts[1] || null };
}
function pathFor(lc, route, arg) {
  return "/" + lc + "/" + (route === "home" && !arg ? "" : route + (arg ? "/" + arg : ""));
}
// Back-compat: migrate legacy hash URLs (#/en/pricing) to clean paths
// (/en/pricing) so old bookmarks and shared links keep working after the switch.
function migrateHashUrl() {
  const raw = (window.location.hash || "").replace(/^#\/?/, "");
  if (raw) { window.history.replaceState(null, "", "/" + raw + window.location.search); return true; }
  return false;
}

function MarketingApp() {
  const { useState, useEffect } = React;
  const [loc, setLoc] = useState(() => { migrateHashUrl(); return parsePath(); });
  const [locale, setLocaleState] = useState(() => loc.locale);
  const t = window.MKT.makeT(locale);
  window.MKT._loc = locale;

  useEffect(() => {
    const onPop = () => { const p = parsePath(); setLoc(p); setLocaleState(p.locale); window.scrollTo(0, 0); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navTo = (lc, route, arg) => {
    const path = pathFor(lc, route, arg);
    if (window.location.pathname !== path) window.history.pushState(null, "", path);
    setLoc({ locale: lc, route, arg }); setLocaleState(lc); window.scrollTo(0, 0);
  };
  // D-212 F3: gömülen içerik tek dile dilimlendiği için dil değiştirme artık
  // istemci tarafında değil TAM GEZİNME. Hedef dilin sayfası zaten prerender'lı
  // olduğundan ek istek maliyeti yok; içerik doğrudan statik HTML'den geliyor.
  const setLocale = (l) => {
    localStorage.setItem("mkt-locale", l);
    window.location.assign(pathFor(l, loc.route, loc.arg));
  };
  const go = (route, arg) => navTo(locale, route, arg);

  useEffect(() => {
    const want = pathFor(locale, loc.route, loc.arg);
    if (window.location.pathname !== want) { window.history.replaceState(null, "", want); }
  }, [locale, loc.route, loc.arg]);

  // D-212 F4: gömülen veri sayfaya göre kırpılıyor. Kullanıcı BAŞKA bir detay
  // sayfasına geçerse eksik alan tek uçtan tamamlanır ve yeniden çizilir.
  // Prerender'lı ilk açılışta hiç tetiklenmez (o sayfanın verisi zaten tam).
  const [, bump] = useState(0);
  useEffect(() => {
    let alive = true;
    ensureRouteData(loc.route, loc.arg).then((changed) => {
      if (changed && alive) bump((n) => n + 1);
    });
    return () => { alive = false; };
  }, [loc.route, loc.arg]);

  useEffect(() => {
    if (!window.MKT.seo) return;
    // Gerçek route + arg'ı geç: seo.js detay sayfalarında (blog-post/help-article/
    // solution/feature) benzersiz title/desc/canonical + BlogPosting/HowTo üretir.
    window.MKT.seo(loc.route, locale, loc.arg ? { arg: loc.arg } : undefined);
  }, [loc, locale]);

  // Belge yönü (Arapça'da RTL) ve dil, locale değiştikçe ayarlanır.
  useEffect(() => {
    const rtl = !!(window.MKT.isRTL && window.MKT.isRTL(locale));
    document.documentElement.dir = rtl ? "rtl" : "ltr";
    document.documentElement.lang = locale;
    if (document.body) document.body.classList.toggle("mkt-rtl", rtl);
    return () => {
      document.documentElement.dir = "ltr";
      if (document.body) document.body.classList.remove("mkt-rtl");
    };
  }, [locale]);
  // D-214: ikonlar sprite'tan geliyor; createIcons() döngüsüne gerek yok.

  const P = window.MKT.Pages, B = window.MKT.PagesB, Cc = window.MKT.PagesC;
  let page;
  switch (loc.route) {
    case "features": page = <P.FeaturesPage t={t} go={go} locale={locale} anchor={loc.arg} />; break;
    case "feature": page = <window.MKT.PagesF.FeatureDetailPage t={t} go={go} locale={locale} id={loc.arg} />; break;
    case "solutions": page = <P.SolutionsPage t={t} go={go} locale={locale} />; break;
    case "solution": page = <window.MKT.PagesS.SolutionDetailPage t={t} go={go} locale={locale} id={loc.arg} />; break;
    case "pricing": page = <B.PricingPage t={t} go={go} locale={locale} />; break;
    case "blog": page = <Cc.BlogPage t={t} go={go} locale={locale} />; break;
    case "blog-post": page = <Cc.BlogPostPage t={t} go={go} locale={locale} slug={loc.arg} />; break;
    case "faq": page = <Cc.FaqPage t={t} go={go} locale={locale} />; break;
    case "help": page = <Cc.HelpPage t={t} go={go} locale={locale} cat={loc.arg} />; break;
    case "help-article": page = <Cc.HelpArticlePage t={t} go={go} locale={locale} slug={loc.arg} />; break;
    case "about": page = <Cc.AboutPage t={t} go={go} locale={locale} />; break;
    case "contact": page = <Cc.ContactPage t={t} go={go} locale={locale} />; break;
    case "legal": page = <Cc.LegalDocPage t={t} go={go} locale={locale} slug={loc.arg} />; break;
    case "newsletter-confirm": page = <Cc.NewsletterResultPage t={t} go={go} locale={locale} mode="confirm" token={loc.arg} />; break;
    case "unsubscribe": page = <Cc.NewsletterResultPage t={t} go={go} locale={locale} mode="unsubscribe" token={loc.arg} />; break;
    case "login": page = <B.LoginPage t={t} go={go} locale={locale} />; break;
    case "register": page = <B.RegisterPage t={t} go={go} locale={locale} />; break;
    default: page = <P.HomePage t={t} go={go} locale={locale} />;
  }

  const isAuth = loc.route === "login" || loc.route === "register";
  const { Header, Footer } = window.MKT.UI;
  return (
    <div className="mk">
      <Header route={loc.route} go={go} locale={locale} setLocale={setLocale} t={t} />
      <main>{page}</main>
      {!isAuth && <Footer go={go} t={t} locale={locale} />}
    </div>
  );
}

export default function MarketingV2() {
  const [ready, setReady] = React.useState(false);
  React.useEffect(() => {
    if (!bootPromise) bootPromise = bootDesign();
    let alive = true;
    bootPromise.then(() => { if (alive) setReady(true); }).catch((e) => { console.error("MarketingV2 boot failed", e); });
    return () => { alive = false; };
  }, []);
  if (!ready) return <div className="mk" style={{ minHeight: "100vh", background: "var(--color-canvas, #fff)" }} />;
  return <MarketingApp />;
}
